import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { apiHelpers } from '@/lib/api';
import type { BlogSuggestion } from '@/types';
import {
  ArrowLeft,
  Lightbulb,
  Loader2,
  RefreshCw,
  Sparkles,
  X,
  ExternalLink,
  TrendingUp,
  Briefcase,
  Pencil,
  Check,
  Plus,
  Trash2,
} from 'lucide-react';
import type { IndustryProfile } from '@/types';

/**
 * Trend-Driven Blog Suggestions — browse weekly-researched topic ideas and
 * generate the full post with one click. Suggestion-first: nothing is published
 * here unless the admin opts into auto-publish in blog settings.
 */
export default function BlogSuggestions() {
  const { projectId } = useParams();
  const queryClient = useQueryClient();
  const [busyId, setBusyId] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['blog-suggestions', projectId],
    queryFn: () => apiHelpers.getBlogSuggestions(projectId!, { status: 'pending' }),
    enabled: Boolean(projectId),
  });

  const payload = data?.data?.data;
  const suggestions = payload?.suggestions ?? [];
  const usage = payload?.usage;

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: ['blog-suggestions', projectId] });

  const refresh = useMutation({
    mutationFn: () => apiHelpers.refreshBlogSuggestions(projectId!),
    onSuccess: (res) => {
      const n = res.data?.data?.created ?? 0;
      toast.success(
        n > 0
          ? `Researched ${n} new topic idea${n === 1 ? '' : 's'}`
          : 'No new ideas right now — you may be at your monthly limit.'
      );
      invalidate();
    },
    onError: () => toast.error('Could not research new suggestions. Try again shortly.'),
  });

  const generate = useMutation({
    mutationFn: ({ id, publish }: { id: string; publish: boolean }) =>
      apiHelpers.generateBlogSuggestion(projectId!, id, publish),
    onMutate: (v) => setBusyId(v.id),
    onSuccess: (res) => {
      const status = res.data?.data?.status;
      toast.success(
        status === 'published'
          ? 'Post generated & published!'
          : 'Draft generated — review it in Posts.'
      );
      invalidate();
    },
    onError: (err: any) => {
      const msg = err?.response?.data?.error?.message || 'Generation failed.';
      toast.error(msg);
    },
    onSettled: () => setBusyId(null),
  });

  const dismiss = useMutation({
    mutationFn: (id: string) => apiHelpers.dismissBlogSuggestion(projectId!, id),
    onSuccess: invalidate,
  });

  return (
    <div className="mx-auto max-w-5xl px-4 py-6">
      <Link
        to={`/projects/${projectId}/blog`}
        className="text-muted-foreground hover:text-foreground mb-4 inline-flex items-center gap-1 text-sm"
      >
        <ArrowLeft className="h-4 w-4" /> Back to Blog
      </Link>

      <div className="mb-6 flex items-start justify-between gap-4">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-semibold">
            <Lightbulb className="h-6 w-6 text-amber-500" /> Trend-Driven Suggestions
          </h1>
          <p className="text-muted-foreground mt-1 max-w-2xl text-sm">
            Each week we research what is trending in your industry across Google Trends, Reddit,
            Substack and industry RSS, and queue ready-to-write blog topics. Click{' '}
            <strong>Generate</strong> to write the full post (with images) in one go.
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <button
            onClick={() => setAdding((v) => !v)}
            className="bg-foreground text-background inline-flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium hover:opacity-90"
          >
            <Plus className="h-4 w-4" />
            Add topic
          </button>
          <button
            onClick={() => refresh.mutate()}
            disabled={refresh.isPending}
            className="hover:bg-accent inline-flex items-center gap-2 rounded-lg border px-3 py-2 text-sm font-medium disabled:opacity-50"
          >
            {refresh.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <RefreshCw className="h-4 w-4" />
            )}
            Research now
          </button>
        </div>
      </div>

      {adding && (
        <AddTopicForm
          projectId={projectId!}
          onDone={() => {
            setAdding(false);
            invalidate();
          }}
          onCancel={() => setAdding(false)}
        />
      )}

      <IndustryProfileCard projectId={projectId!} />

      {usage && (
        <div className="mb-6 grid grid-cols-2 gap-3">
          <Meter
            label="Suggestions this month"
            used={usage.suggestions.used}
            limit={usage.suggestions.limit}
            tone="amber"
          />
          <Meter
            label="AI posts generated this month"
            used={usage.published.used}
            limit={usage.published.limit}
            tone="emerald"
          />
        </div>
      )}

      {isLoading ? (
        <div className="text-muted-foreground flex items-center justify-center py-20">
          <Loader2 className="h-6 w-6 animate-spin" />
        </div>
      ) : suggestions.length === 0 ? (
        <div className="rounded-xl border border-dashed py-16 text-center">
          <TrendingUp className="text-muted-foreground mx-auto mb-3 h-10 w-10" />
          <p className="font-medium">No pending suggestions yet</p>
          <p className="text-muted-foreground mt-1 text-sm">
            New ideas are researched weekly. Hit <strong>Research now</strong> to pull a fresh
            batch.
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {suggestions.map((s) => (
            <SuggestionCard
              key={s.id}
              s={s}
              busy={busyId === s.id}
              disabled={generate.isPending || (usage ? usage.published.remaining <= 0 : false)}
              onGenerate={(publish) => generate.mutate({ id: s.id, publish })}
              onDismiss={() => dismiss.mutate(s.id)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function Meter({
  label,
  used,
  limit,
  tone,
}: {
  label: string;
  used: number;
  limit: number;
  tone: 'amber' | 'emerald';
}) {
  const pct = limit > 0 ? Math.min(100, Math.round((used / limit) * 100)) : 0;
  const bar = tone === 'amber' ? 'bg-amber-500' : 'bg-emerald-500';
  return (
    <div className="rounded-lg border p-3">
      <div className="flex items-center justify-between text-sm">
        <span className="text-muted-foreground">{label}</span>
        <span className="font-medium">
          {used} / {limit}
        </span>
      </div>
      <div className="bg-muted mt-2 h-1.5 overflow-hidden rounded-full">
        <div className={`h-full ${bar}`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

function SuggestionCard({
  s,
  busy,
  disabled,
  onGenerate,
  onDismiss,
}: {
  s: BlogSuggestion;
  busy: boolean;
  disabled: boolean;
  onGenerate: (publish: boolean) => void;
  onDismiss: () => void;
}) {
  return (
    <div className="hover:border-foreground/20 rounded-xl border p-4 transition-colors">
      <div className="flex items-start justify-between gap-3">
        <h3 className="font-medium leading-snug">{s.title}</h3>
        <button
          onClick={onDismiss}
          title="Dismiss"
          className="text-muted-foreground hover:text-foreground shrink-0"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      {s.source === 'topic-engine' && s.engine && (
        <div className="mt-1 flex flex-wrap items-center gap-2 text-[11px]">
          <span className="rounded-full bg-blue-100 px-2 py-0.5 font-medium text-blue-800 dark:bg-blue-900/30 dark:text-blue-300">
            Topic engine · {s.engine.archetype}
          </span>
          <span className="text-muted-foreground">
            cluster {s.engine.clusterName} · target “{s.engine.targetQuery}”
            {s.engine.scores
              ? ` · score ${s.engine.scores.priority.toFixed(2)} (win ${s.engine.scores.winnability.toFixed(2)})`
              : ''}
          </span>
        </div>
      )}
      {s.source === 'manual' && (
        <div className="mt-1 flex flex-wrap items-center gap-2 text-[11px]">
          <span className="rounded-full bg-emerald-100 px-2 py-0.5 font-medium text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300">
            Your topic · written first
          </span>
        </div>
      )}
      {s.source === 'aeo' && s.aeo && (
        <div className="mt-1 flex flex-wrap items-center gap-2 text-[11px]">
          <span className="rounded-full bg-violet-100 px-2 py-0.5 font-medium text-violet-800 dark:bg-violet-900/30 dark:text-violet-300">
            From AI Answer Visibility · answer-first format
          </span>
          <span className="text-muted-foreground">buyer question “{s.aeo.question}”</span>
        </div>
      )}
      {s.angle && <p className="text-muted-foreground mt-1 text-sm">{s.angle}</p>}
      {s.rationale && (
        <p className="mt-2 flex items-start gap-1 text-xs text-amber-700 dark:text-amber-400">
          <TrendingUp className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          {s.rationale}
        </p>
      )}

      {s.keywords?.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-1.5">
          {s.keywords.slice(0, 6).map((k) => (
            <span key={k} className="bg-muted rounded-full px-2 py-0.5 text-xs">
              {k}
            </span>
          ))}
        </div>
      )}

      {s.signals?.length > 0 && (
        <div className="text-muted-foreground mt-3 text-xs">
          <span className="font-medium">
            {s.source === 'manual' ? 'Sources to cite:' : 'Why now:'}
          </span>{' '}
          {s.signals.slice(0, s.source === 'manual' ? 8 : 3).map((sig, i) => (
            <span key={i}>
              {i > 0 && ' · '}
              {sig.url ? (
                <a
                  href={sig.url}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-0.5 hover:underline"
                >
                  {sig.term}
                  <ExternalLink className="h-3 w-3" />
                </a>
              ) : (
                sig.term
              )}
              <span className="opacity-60"> ({sig.source})</span>
            </span>
          ))}
        </div>
      )}

      <div className="mt-4 flex items-center gap-2">
        <button
          onClick={() => onGenerate(false)}
          disabled={busy || disabled}
          className="bg-foreground text-background inline-flex items-center gap-2 rounded-lg px-3 py-1.5 text-sm font-medium hover:opacity-90 disabled:opacity-50"
        >
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
          Generate draft
        </button>
        <button
          onClick={() => onGenerate(true)}
          disabled={busy || disabled}
          className="hover:bg-accent inline-flex items-center gap-2 rounded-lg border px-3 py-1.5 text-sm font-medium disabled:opacity-50"
        >
          Generate & publish
        </button>
      </div>
    </div>
  );
}

type SourceRow = { term: string; source: string; url: string };
const EMPTY_SOURCE: SourceRow = { term: '', source: '', url: '' };

/**
 * Add topic: queue the admin's own idea for the Editorial Desk. It is written
 * before any researched suggestion, does not use the monthly suggestion
 * allowance, and every source listed here is cited in the post.
 */
function AddTopicForm({
  projectId,
  onDone,
  onCancel,
}: {
  projectId: string;
  onDone: () => void;
  onCancel: () => void;
}) {
  const [title, setTitle] = useState('');
  const [angle, setAngle] = useState('');
  const [keywords, setKeywords] = useState('');
  const [rationale, setRationale] = useState('');
  const [productToFeature, setProductToFeature] = useState('');
  const [sources, setSources] = useState<SourceRow[]>([]);

  const filledSources = sources.filter((r) => r.url.trim());
  const sourcesValid = filledSources.every(
    (r) => r.term.trim() && /^https?:\/\//i.test(r.url.trim())
  );
  const canSave = title.trim().length >= 5 && splitList(keywords).length > 0 && sourcesValid;

  const create = useMutation({
    mutationFn: () =>
      apiHelpers.createBlogSuggestion(projectId, {
        title: title.trim(),
        angle: angle.trim() || undefined,
        keywords: splitList(keywords),
        rationale: rationale.trim() || undefined,
        productToFeature: productToFeature.trim() || undefined,
        signals: filledSources.length
          ? filledSources.map((r) => ({
              term: r.term.trim(),
              url: r.url.trim(),
              source: r.source.trim() || hostLabel(r.url),
            }))
          : undefined,
      }),
    onSuccess: () => {
      toast.success('Topic added. It will be written before any researched suggestion.');
      onDone();
    },
    onError: (err: any) =>
      toast.error(err?.response?.data?.error?.message || 'Could not add the topic.'),
  });

  const setRow = (i: number, patch: Partial<SourceRow>) =>
    setSources((rows) => rows.map((r, j) => (j === i ? { ...r, ...patch } : r)));

  return (
    <div className="mb-6 rounded-xl border p-4">
      <div className="flex items-center gap-2 font-medium">
        <Plus className="h-4 w-4 text-emerald-500" /> Add your own topic
      </div>
      <p className="text-muted-foreground mt-1 text-xs">
        Queued ahead of researched ideas and does not use your monthly suggestion allowance.
        Generating it counts as one AI post. Every source you list is cited in the post.
      </p>
      <div className="mt-3 space-y-3">
        <Field label="Working title *">
          <input
            className="bg-background w-full rounded-lg border px-3 py-1.5 text-sm"
            value={title}
            maxLength={200}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="e.g. Cookieless analytics: what you can still measure"
          />
        </Field>
        <Field label="Angle (one sentence)">
          <input
            className="bg-background w-full rounded-lg border px-3 py-1.5 text-sm"
            value={angle}
            maxLength={500}
            onChange={(e) => setAngle(e.target.value)}
          />
        </Field>
        <Field label="Target keywords (comma-separated; the first is the search the post must win) *">
          <input
            className="bg-background w-full rounded-lg border px-3 py-1.5 text-sm"
            value={keywords}
            onChange={(e) => setKeywords(e.target.value)}
            placeholder="cookieless analytics, first-party data"
          />
        </Field>
        <Field label="Why this topic (for your team, optional)">
          <textarea
            className="bg-background w-full rounded-lg border px-3 py-1.5 text-sm"
            rows={2}
            maxLength={1000}
            value={rationale}
            onChange={(e) => setRationale(e.target.value)}
          />
        </Field>
        <Field label="Product or feature to mention (optional)">
          <input
            className="bg-background w-full rounded-lg border px-3 py-1.5 text-sm"
            value={productToFeature}
            maxLength={200}
            onChange={(e) => setProductToFeature(e.target.value)}
          />
        </Field>

        <div>
          <span className="text-muted-foreground mb-1 block text-xs font-medium">
            Sources the post must cite (up to 8)
          </span>
          <div className="space-y-2">
            {sources.map((r, i) => (
              <div key={i} className="grid gap-2 sm:grid-cols-[2fr_1fr_2fr_auto]">
                <input
                  className="bg-background rounded-lg border px-3 py-1.5 text-sm"
                  placeholder="Claim this source backs"
                  maxLength={300}
                  value={r.term}
                  onChange={(e) => setRow(i, { term: e.target.value })}
                />
                <input
                  className="bg-background rounded-lg border px-3 py-1.5 text-sm"
                  placeholder="Source name"
                  maxLength={120}
                  value={r.source}
                  onChange={(e) => setRow(i, { source: e.target.value })}
                />
                <input
                  className="bg-background rounded-lg border px-3 py-1.5 text-sm"
                  placeholder="https://…"
                  type="url"
                  value={r.url}
                  onChange={(e) => setRow(i, { url: e.target.value })}
                />
                <button
                  onClick={() => setSources((rows) => rows.filter((_, j) => j !== i))}
                  title="Remove source"
                  className="text-muted-foreground hover:text-foreground px-1"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>
          {sources.length < 8 && (
            <button
              onClick={() => setSources((rows) => [...rows, { ...EMPTY_SOURCE }])}
              className="text-muted-foreground hover:text-foreground mt-2 inline-flex items-center gap-1 text-sm"
            >
              <Plus className="h-3.5 w-3.5" /> Add source
            </button>
          )}
          {!sourcesValid && (
            <p className="mt-1 text-xs text-red-600">
              Each source needs the claim it backs and an http(s) link.
            </p>
          )}
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={() => create.mutate()}
            disabled={!canSave || create.isPending}
            className="bg-foreground text-background inline-flex items-center gap-2 rounded-lg px-3 py-1.5 text-sm font-medium disabled:opacity-50"
          >
            {create.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Check className="h-4 w-4" />
            )}
            Add topic
          </button>
          <button
            onClick={onCancel}
            className="hover:bg-accent rounded-lg border px-3 py-1.5 text-sm"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

function hostLabel(url: string): string {
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch {
    return 'Source';
  }
}

/**
 * Industry Profile — auto-deduced from the site's /llms.txt, shown for
 * visibility and editable so the business owner can correct it. Drives what the
 * suggestions engine researches and how generated posts are framed.
 */
function IndustryProfileCard({ projectId }: { projectId: string }) {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<{
    industry: string;
    productCategories: string;
    targetAudience: string;
    keywords: string;
  } | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['industry-profile', projectId],
    queryFn: () => apiHelpers.getIndustryProfile(projectId),
    enabled: Boolean(projectId),
  });
  const profile: IndustryProfile | undefined = data?.data?.data?.profile;

  const save = useMutation({
    mutationFn: () =>
      apiHelpers.updateIndustryProfile(projectId, {
        industry: draft!.industry.trim(),
        productCategories: splitList(draft!.productCategories),
        targetAudience: draft!.targetAudience.trim() || undefined,
        keywords: splitList(draft!.keywords),
      }),
    onSuccess: () => {
      toast.success('Business profile updated');
      setEditing(false);
      queryClient.invalidateQueries({ queryKey: ['industry-profile', projectId] });
    },
    onError: () => toast.error('Could not save the profile.'),
  });

  const startEdit = () => {
    setDraft({
      industry: profile?.industry || '',
      productCategories: (profile?.productCategories || []).join(', '),
      targetAudience: profile?.targetAudience || '',
      keywords: (profile?.keywords || []).join(', '),
    });
    setEditing(true);
  };

  return (
    <div className="mb-6 rounded-xl border p-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 font-medium">
          <Briefcase className="h-4 w-4 text-sky-500" /> Business Profile
          {profile && !profile.editedByUser && (
            <span className="bg-muted text-muted-foreground rounded-full px-2 py-0.5 text-xs">
              auto-detected
            </span>
          )}
          {profile?.editedByUser && (
            <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-xs text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300">
              edited by you
            </span>
          )}
        </div>
        {!editing && !isLoading && (
          <button
            onClick={startEdit}
            className="text-muted-foreground hover:text-foreground inline-flex items-center gap-1 text-sm"
          >
            <Pencil className="h-3.5 w-3.5" /> Edit
          </button>
        )}
      </div>

      <p className="text-muted-foreground mt-1 text-xs">
        Auto-detected from your site so suggestions target your real niche. Edit if it&apos;s not
        quite right — your version is kept.
      </p>

      {isLoading ? (
        <div className="text-muted-foreground py-6 text-center">
          <Loader2 className="mx-auto h-5 w-5 animate-spin" />
        </div>
      ) : editing && draft ? (
        <div className="mt-3 space-y-3">
          <Field label="Industry / niche">
            <input
              className="bg-background w-full rounded-lg border px-3 py-1.5 text-sm"
              value={draft.industry}
              onChange={(e) => setDraft({ ...draft, industry: e.target.value })}
              placeholder="e.g. reconstituted veneer manufacturer"
            />
          </Field>
          <Field label="Product / service categories (comma-separated)">
            <input
              className="bg-background w-full rounded-lg border px-3 py-1.5 text-sm"
              value={draft.productCategories}
              onChange={(e) => setDraft({ ...draft, productCategories: e.target.value })}
            />
          </Field>
          <Field label="Target audience">
            <textarea
              className="bg-background w-full rounded-lg border px-3 py-1.5 text-sm"
              rows={2}
              value={draft.targetAudience}
              onChange={(e) => setDraft({ ...draft, targetAudience: e.target.value })}
            />
          </Field>
          <Field label="Keywords (comma-separated)">
            <input
              className="bg-background w-full rounded-lg border px-3 py-1.5 text-sm"
              value={draft.keywords}
              onChange={(e) => setDraft({ ...draft, keywords: e.target.value })}
            />
          </Field>
          <div className="flex items-center gap-2">
            <button
              onClick={() => save.mutate()}
              disabled={save.isPending || !draft.industry.trim()}
              className="bg-foreground text-background inline-flex items-center gap-2 rounded-lg px-3 py-1.5 text-sm font-medium disabled:opacity-50"
            >
              {save.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Check className="h-4 w-4" />
              )}
              Save
            </button>
            <button
              onClick={() => setEditing(false)}
              className="hover:bg-accent rounded-lg border px-3 py-1.5 text-sm"
            >
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <dl className="mt-3 grid gap-2 text-sm sm:grid-cols-2">
          <Row label="Industry" value={profile?.industry} />
          <Row label="Audience" value={profile?.targetAudience} />
          <Row label="Categories" value={(profile?.productCategories || []).join(', ')} />
          <Row label="Keywords" value={(profile?.keywords || []).join(', ')} />
        </dl>
      )}
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="text-muted-foreground mb-1 block text-xs font-medium">{label}</span>
      {children}
    </label>
  );
}

function Row({ label, value }: { label: string; value?: string }) {
  return (
    <div>
      <dt className="text-muted-foreground text-xs">{label}</dt>
      <dd className="mt-0.5">{value || <span className="text-muted-foreground">—</span>}</dd>
    </div>
  );
}

function splitList(s: string): string[] {
  return s
    .split(',')
    .map((x) => x.trim())
    .filter(Boolean);
}
